import { useState, useEffect } from 'react';
import { Sensitive } from '../components/Sensitive';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Users, ChevronRight, ChevronDown, TrendingUp, UserPlus, Mail, Phone, Calendar, Clock, Zap, Network, Building2, MapPin, CheckCircle, MoreVertical, Download, Workflow, Search, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import OrgChart from '../components/OrgChart';
import { ReassignAgentModal, ToggleStatusModal, ChangeCommissionModal, DeleteUserModal, EditAgentDetailsModal } from '../components/AdminControlModals';
import { ExportModal } from '../components/ExportModal';
import { PageHeader } from '../components/dashboard/PageHeader';
import { StatCard } from '../components/dashboard/StatCard';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TreeNode({ node, level = 0, onAgentClick }) {
  const [expanded, setExpanded] = useState(level < 2);

  // Hide disabled users from the active hierarchy. (They're listed in the
  // dedicated "Disabled" tab so admins can re-enable them.)
  if (node.status && node.status !== 'active') return null;
  
  // Active children only — disabled children stay out of the active tree
  const visibleChildren = (node.children || []).filter(
    (child) => !child.status || child.status === 'active'
  );

  return (
    <div className={level > 0 ? "ml-5 border-l border-[#E9EDF7] pl-3" : ""} data-testid={`tree-node-${node.id}`}>
      <div
        className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-[#F4F7FE] cursor-pointer group transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {visibleChildren.length > 0 ? (
          <div className="p-0.5">
            {expanded ? <ChevronDown className="h-4 w-4 text-[#A3AED0]" /> : <ChevronRight className="h-4 w-4 text-[#A3AED0]" />}
          </div>
        ) : (
          <div className="w-5" />
        )}
        <div className="flex-1 min-w-0" onClick={(e) => { e.stopPropagation(); onAgentClick(node); }}>
          <div className="font-medium text-sm text-[#2B3674] group-hover:text-[#1E3FBF] truncate">
            {node.name}
          </div>
          <div className="text-xs text-[#A3AED0]">
            {node.role} • <Sensitive>{node.comp_percentage}%</Sensitive>{node.team_size > 0 && ` • ${node.team_size} team`}
          </div>
        </div>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
          Active
        </span>
      </div>
      <AnimatePresence>
        {expanded && visibleChildren.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {visibleChildren.map((child) => (
              <TreeNode key={child.id} node={child} level={level + 1} onAgentClick={onAgentClick} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function MyTeam() {
  const { user, getAuthHeader, impersonateUser } = useAuth();
  const [hierarchyData, setHierarchyData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [agentDetails, setAgentDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Admin controls state
  const [orgTreeData, setOrgTreeData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editDetailsModalOpen, setEditDetailsModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API}/hierarchy/tree`, getAuthHeader()),
        axios.get(`${API}/hierarchy/downline`, getAuthHeader()),
        axios.get(`${API}/hierarchy/stats`, getAuthHeader())
      ];

      // If admin, fetch additional data
      if (user?.role === 'admin') {
        promises.push(
          axios.get(`${API}/admin/org-tree`, getAuthHeader()),
          axios.get(`${API}/admin/users`, getAuthHeader())
        );
      }

      const results = await Promise.all(promises);
      
      setHierarchyData(results[0].data);
      
      // Sort table data alphabetically by first name
      const sortedTableData = (results[1].data || []).sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setTableData(sortedTableData);
      setFilteredTableData(sortedTableData);
      setStats(results[2].data);
      
      if (user?.role === 'admin') {
        setOrgTreeData(results[3].data);
        setAllUsers(results[4].data);
      }
    } catch (error) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Handle search in table view — by default, hide DISABLED users from this list.
  // Disabled users live in the dedicated "Disabled" tab so the active hierarchy stays clean.
  useEffect(() => {
    const activeOnly = (tableData || []).filter(
      (m) => !m.status || m.status === 'active'
    );
    if (!searchQuery.trim()) {
      setFilteredTableData(activeOnly);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = activeOnly.filter(member => 
        (member.name || '').toLowerCase().includes(query) ||
        (member.email || '').toLowerCase().includes(query) ||
        (member.npn || '').toLowerCase().includes(query)
      );
      setFilteredTableData(filtered);
    }
  }, [searchQuery, tableData]);
  
  // Derived list of disabled users for the "Disabled" tab
  const disabledMembers = (tableData || []).filter((m) => m.status === 'disabled');

  const handleAgentClick = async (agent) => {
    setDrawerOpen(true);
    setLoadingDetails(true);
    
    try {
      const endpoint = user?.role === 'admin' ? `${API}/users/${agent.id}/full-profile` : `${API}/users/${agent.id}`;
      const response = await axios.get(endpoint, getAuthHeader());
      setAgentDetails(response.data);
    } catch (error) {
      toast.error('Failed to load agent details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Admin action handlers
  const handleReassignAgent = async (agentId, newUplineId) => {
    setModalLoading(true);
    try {
      await axios.put(
        `${API}/admin/users/${agentId}/upline`,
        { new_upline_id: newUplineId },
        getAuthHeader()
      );
      toast.success('Agent reassigned successfully');
      setReassignModalOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign agent');
    } finally {
      setModalLoading(false);
    }
  };

  const handleToggleStatus = async (agentId, newStatus) => {
    setModalLoading(true);
    try {
      await axios.put(
        `${API}/admin/users/${agentId}/status`,
        { status: newStatus },
        getAuthHeader()
      );
      toast.success(`Agent ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      setStatusModalOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent status');
    } finally {
      setModalLoading(false);
    }
  };

  const handleChangeCommission = async (agentId, newCommission) => {
    setModalLoading(true);
    try {
      await axios.put(
        `${API}/admin/users/${agentId}/commission`,
        { comp_percentage: newCommission },
        getAuthHeader()
      );
      toast.success('Commission updated successfully');
      setCommissionModalOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commission');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditAgentDetails = async (agentId, updates) => {
    setModalLoading(true);
    try {
      const response = await axios.put(
        `${API}/admin/users/${agentId}/details`,
        updates,
        getAuthHeader()
      );
      toast.success(response.data.message || 'Agent details updated successfully');
      setEditDetailsModalOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent details');
    } finally {
      setModalLoading(false);
    }
  };

  const handleExportHierarchy = async (format) => {
    setModalLoading(true);
    try {
      const response = await axios.get(`${API}/admin/hierarchy-export`, getAuthHeader());
      const data = response.data;
      const timestamp = new Date().toISOString().split('T')[0];

      if (format === 'json') {
        // JSON Export
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        downloadFile(blob, `hierarchy-export-${timestamp}.json`);
      } else if (format === 'csv') {
        // CSV Export
        const headers = ['Name', 'Email', 'Role', 'Status', 'Commission %', 'Upline', 'NPN', 'Date Joined'];
        const rows = data.users.map(u => [
          u.name,
          u.email,
          u.role,
          u.status,
          u.comp_percentage,
          u.upline_name || 'None',
          u.npn || '',
          u.date_joined || ''
        ]);
        
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        downloadFile(blob, `hierarchy-export-${timestamp}.csv`);
      } else if (format === 'xlsx') {
        // XLSX Export
        const worksheet = XLSX.utils.json_to_sheet(
          data.users.map(u => ({
            'Name': u.name,
            'Email': u.email,
            'Role': u.role,
            'Status': u.status,
            'Commission %': u.comp_percentage,
            'Upline': u.upline_name || 'None',
            'NPN': u.npn || '',
            'Date Joined': u.date_joined || ''
          }))
        );
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Hierarchy');
        
        // Add metadata sheet
        const metaSheet = XLSX.utils.json_to_sheet([{
          'Exported At': data.exported_at,
          'Exported By': data.exported_by,
          'Total Users': data.total_users
        }]);
        XLSX.utils.book_append_sheet(workbook, metaSheet, 'Metadata');
        
        XLSX.writeFile(workbook, `hierarchy-export-${timestamp}.xlsx`);
      } else if (format === 'pdf') {
        // PDF Export (Simplified to avoid timeout)
        const doc = new jsPDF();
        
        // Title
        doc.setFontSize(20);
        doc.setTextColor(6, 182, 212);
        doc.text('Atlas - Hierarchy Export', 14, 20);
        
        // Metadata
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Exported: ${new Date(data.exported_at).toLocaleString()}`, 14, 32);
        doc.text(`By: ${data.exported_by}`, 14, 38);
        doc.text(`Total Users: ${data.total_users}`, 14, 44);
        
        // Table with basic data
        const tableData = data.users.slice(0, 50).map(u => [
          u.name,
          u.role,
          `${u.comp_percentage}%`,
          u.status
        ]);
        
        doc.autoTable({
          startY: 52,
          head: [['Name', 'Role', 'Commission', 'Status']],
          body: tableData,
          styles: { 
            fontSize: 9,
            cellPadding: 2
          },
          headStyles: { 
            fillColor: [6, 182, 212],
            textColor: 255
          },
          alternateRowStyles: {
            fillColor: [245, 247, 250]
          },
          margin: { top: 52 }
        });
        
        // Add note if data was truncated
        if (data.users.length > 50) {
          const finalY = doc.lastAutoTable.finalY || 52;
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(`Note: Showing first 50 of ${data.users.length} users. Download XLSX for complete data.`, 14, finalY + 10);
        }
        
        doc.save(`hierarchy-export-${timestamp}.pdf`);
      }
      
      setExportModalOpen(false);
      toast.success(`Hierarchy exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export hierarchy');
    } finally {
      setModalLoading(false);
    }
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const openAdminMenu = (agent, action) => {
    setSelectedAgent(agent);
    if (action === 'reassign') setReassignModalOpen(true);
    else if (action === 'status') setStatusModalOpen(true);
    else if (action === 'commission') setCommissionModalOpen(true);
    else if (action === 'edit') setEditDetailsModalOpen(true);
    else if (action === 'impersonate') handleImpersonate(agent);
    else if (action === 'delete') setDeleteModalOpen(true);
    else if (action === 'reset-password') handleForceResetPassword(agent);
  };

  const handleForceResetPassword = async (agent) => {
    if (!window.confirm(
      `Reset password for ${agent.name}?\n\nA temporary password will be generated and emailed to ${agent.email}. They will be required to change it on next login.`
    )) {
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/users/${agent.id}/force-reset-password`,
        {},
        getAuthHeader()
      );
      const tempPw = res.data.temporary_password;
      const emailStatus = res.data.email_status;
      // Show the temp password in a copyable confirm so the admin can also share it manually
      const msg =
        `Password reset for ${agent.name}.\n\n` +
        `Temporary password: ${tempPw}\n\n` +
        `Email delivery: ${emailStatus === 'sent' ? '✓ sent to ' + res.data.user_email : '✗ ' + emailStatus}\n\n` +
        `Click OK to copy the temporary password to your clipboard.`;
      if (window.confirm(msg)) {
        try {
          await navigator.clipboard.writeText(tempPw);
          toast.success('Temporary password copied to clipboard');
        } catch {
          toast.success('Password reset complete');
        }
      } else {
        toast.success('Password reset complete');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleImpersonate = async (agent) => {
    try {
      await impersonateUser(agent.id);
      toast.success(`Now viewing as ${agent.name}`);
      // Redirect to dashboard
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      toast.error('Failed to impersonate user');
    }
  };

  const handleDeleteUser = async (agentId) => {
    setModalLoading(true);
    try {
      const response = await axios.delete(
        `${API}/admin/users/${agentId}`,
        getAuthHeader()
      );
      toast.success(response.data.message || 'User deleted successfully');
      setDeleteModalOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setModalLoading(false);
    }
  };

  const isAdmin = user?.role === 'admin';
  const canViewLastLogin = user?.role === 'admin' || user?.role === 'leader';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#2447D6] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="my-team-page">
      {/* Header */}
      <PageHeader
        title="My Team"
        subtitle="Manage hierarchy and performance"
      />

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { title: 'Direct', value: stats.direct_agents, icon: Users, color: 'blue' },
            { title: 'Indirect', value: stats.indirect_agents, icon: TrendingUp, color: 'purple' },
            { title: 'Total', value: stats.total_downline, icon: Network, color: 'orange' },
            { title: 'New', value: stats.new_this_month, icon: UserPlus, color: 'green' }
          ].map((stat, i) => (
            <StatCard
              key={stat.title}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
              index={i}
              testId={`team-stat-${stat.title.toLowerCase()}`}
            />
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="tree" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-[#E9EDF7] p-1 h-auto">
            <TabsTrigger value="tree" data-testid="tree-view-tab" className="data-[state=active]:bg-white data-[state=active]:text-[#1E3FBF] data-[state=active]:shadow-sm px-4 py-2 text-sm">
              <Network className="h-4 w-4 mr-1.5" />Tree
            </TabsTrigger>
            <TabsTrigger value="table" data-testid="table-view-tab" className="data-[state=active]:bg-white data-[state=active]:text-[#1E3FBF] data-[state=active]:shadow-sm px-4 py-2 text-sm">
              <Users className="h-4 w-4 mr-1.5" />Table
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="org-chart" data-testid="org-chart-tab" className="data-[state=active]:bg-white data-[state=active]:text-[#1E3FBF] data-[state=active]:shadow-sm px-4 py-2 text-sm">
                <Workflow className="h-4 w-4 mr-1.5" />Org Chart
              </TabsTrigger>
            )}
            <TabsTrigger value="disabled" data-testid="disabled-view-tab" className="data-[state=active]:bg-white data-[state=active]:text-rose-600 data-[state=active]:shadow-sm px-4 py-2 text-sm">
              <Ban className="h-4 w-4 mr-1.5" />
              Disabled
              {disabledMembers.length > 0 && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                  {disabledMembers.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {isAdmin && (
            <Button
              onClick={() => setExportModalOpen(true)}
              variant="outline"
              size="sm"
              className="gap-2"
              data-testid="export-hierarchy-btn"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          )}
        </div>

        <TabsContent value="tree" className="mt-4">
          <Card className="bg-white">
            <CardContent className="p-4">
              {hierarchyData?.trees && hierarchyData.trees.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="min-w-max space-y-1">
                    {hierarchyData.trees.map((tree) => (
                      <TreeNode key={tree.id} node={tree} onAgentClick={handleAgentClick} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Network className="h-10 w-10 mx-auto text-[#A3AED0] mb-3" />
                  <p className="text-[#A3AED0] text-sm">No team members yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="table" className="mt-4">
          <Card className="bg-white">
            {/* Search Bar */}
            <div className="p-4 border-b border-[#E9EDF7]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3AED0]" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or NPN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                  data-testid="team-search-input"
                />
              </div>
              {searchQuery && (
                <p className="text-xs text-[#A3AED0] mt-2">
                  Found {filteredTableData.length} {filteredTableData.length === 1 ? 'member' : 'members'}
                </p>
              )}
            </div>
            
            <CardContent className="p-0">
              {filteredTableData && filteredTableData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="team-table">
                    <thead>
                      <tr className="border-b border-[#E9EDF7]">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">Name</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">Email</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">Role</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">NPN</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">Comp</th>
                        {canViewLastLogin && <th className="text-left py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">Last Login</th>}
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#A3AED0] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTableData.map((member) => (
                        <tr
                          key={member.id}
                          className="border-b border-slate-100 hover:bg-[#F4F7FE] transition-colors"
                          data-testid={`team-member-row-${member.id}`}
                        >
                          <td className="py-3 px-4 text-sm font-medium text-[#2B3674] cursor-pointer" onClick={() => handleAgentClick(member)}>{member.name}</td>
                          <td className="py-3 px-4 text-sm text-[#A3AED0]">{member.email}</td>
                          <td className="py-3 px-4">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#1E3FBF] capitalize">{member.role}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-[#A3AED0] font-mono">{member.npn || '-'}</td>
                          <td className="py-3 px-4 text-sm font-semibold text-[#2B3674]"><Sensitive>{member.comp_percentage}%</Sensitive></td>
                          {canViewLastLogin && (
                            <td className="py-3 px-4 text-xs text-[#A3AED0]">
                              {member.last_login ? new Date(member.last_login).toLocaleDateString() : 'Never'}
                            </td>
                          )}
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              member.status === 'active' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-[#E9EDF7] text-[#A3AED0]'
                            }`}>
                              {member.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`admin-menu-${member.id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {isAdmin && (
                                  <>
                                    <DropdownMenuItem onClick={() => openAdminMenu(member, 'edit')} data-testid="action-edit-details">
                                      Edit Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openAdminMenu(member, 'impersonate')} data-testid="action-impersonate">
                                      View as Agent
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openAdminMenu(member, 'reassign')} data-testid="action-reassign">
                                      Reassign Agent
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openAdminMenu(member, 'commission')} data-testid="action-commission">
                                      Change Commission
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openAdminMenu(member, 'reset-password')} data-testid="action-reset-password">
                                      Reset Password
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => openAdminMenu(member, 'status')} data-testid="action-status">
                                  {member.status === 'active' ? 'Disable' : 'Enable'} Account
                                </DropdownMenuItem>
                                {isAdmin && (
                                  <DropdownMenuItem 
                                    onClick={() => openAdminMenu(member, 'delete')} 
                                    data-testid="action-delete"
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    Delete User
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Users className="h-10 w-10 mx-auto text-[#A3AED0] mb-3" />
                  <p className="text-[#A3AED0] text-sm">No team members yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Org Chart Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="org-chart" className="mt-4">
            <OrgChart data={orgTreeData} onNodeClick={handleAgentClick} />
          </TabsContent>
        )}

        {/* Disabled Accounts — separate bin so they don't clutter the active hierarchy */}
        <TabsContent value="disabled" className="mt-4">
          <Card data-testid="disabled-accounts-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Ban className="h-4 w-4 text-rose-500" />
                <div>
                  <CardTitle className="text-base">Disabled Accounts</CardTitle>
                  <CardDescription>
                    These accounts can&apos;t log in and are hidden from the active Tree/Table views. Their historical production stays counted in your team rollups. Click <strong>Enable</strong> to bring them back.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {disabledMembers.length === 0 ? (
                <div className="py-12 text-center text-sm text-[#A3AED0]">
                  No disabled accounts. Disable an active account from the Tree or Table view and it will appear here.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-[#E9EDF7]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F7FE]">
                      <tr>
                        <th className="text-left py-2 px-3 text-xs font-medium uppercase tracking-wide text-[#A3AED0]">Name</th>
                        <th className="text-left py-2 px-3 text-xs font-medium uppercase tracking-wide text-[#A3AED0] hidden sm:table-cell">Email</th>
                        <th className="text-left py-2 px-3 text-xs font-medium uppercase tracking-wide text-[#A3AED0] hidden md:table-cell">Upline</th>
                        <th className="text-left py-2 px-3 text-xs font-medium uppercase tracking-wide text-[#A3AED0] hidden lg:table-cell">Role</th>
                        <th className="text-right py-2 px-3 text-xs font-medium uppercase tracking-wide text-[#A3AED0]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {disabledMembers.map((m) => (
                        <tr
                          key={m.id}
                          data-testid={`disabled-row-${m.id}`}
                          className="border-t border-slate-100 opacity-80 hover:opacity-100 hover:bg-[#F4F7FE] transition-opacity"
                        >
                          <td className="py-2.5 px-3 font-medium text-[#2B3674]">
                            {m.name}
                          </td>
                          <td className="py-2.5 px-3 text-[#2B3674]/80 hidden sm:table-cell">
                            {m.email}
                          </td>
                          <td className="py-2.5 px-3 text-[#A3AED0] hidden md:table-cell">
                            {m.upline_name || '—'}
                          </td>
                          <td className="py-2.5 px-3 text-[#A3AED0] hidden lg:table-cell">
                            {m.role}
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`enable-btn-${m.id}`}
                              onClick={() => {
                                setSelectedAgent(m);
                                setStatusModalOpen(true);
                              }}
                              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                              Enable
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Agent Profile Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-[#2447D6]" />
              Agent Profile
            </SheetTitle>
          </SheetHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2447D6] border-t-transparent"></div>
            </div>
          ) : agentDetails ? (
            <div className="mt-4 space-y-4">
              {/* Basic Info */}
              <div className="text-center pb-4 border-b border-[#E9EDF7]">
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#2447D6] to-blue-500 flex items-center justify-center text-white text-xl font-bold mb-2">
                  {agentDetails.name?.charAt(0)}
                </div>
                <h3 className="text-lg font-bold text-[#2B3674]">{agentDetails.name}</h3>
                <p className="text-xs text-[#1E3FBF] font-medium uppercase">{agentDetails.role}</p>
              </div>

              {/* Details Grid */}
              <div className="space-y-2">
                <InfoRow icon={Mail} label="Email" value={agentDetails.email} />
                {agentDetails.phone && <InfoRow icon={Phone} label="Phone" value={agentDetails.phone} />}
                {agentDetails.npn && <InfoRow label="NPN" value={agentDetails.npn} mono />}
                <InfoRow icon={Calendar} label="Joined" value={new Date(agentDetails.created_at).toLocaleDateString()} />
                {canViewLastLogin && agentDetails.last_login && (
                  <InfoRow icon={Clock} label="Last Login" value={new Date(agentDetails.last_login).toLocaleString()} />
                )}
                {agentDetails.upline_name && <InfoRow icon={Users} label="Upline" value={agentDetails.upline_name} />}
                <InfoRow icon={Zap} label="Commission" value={<Sensitive>{`${agentDetails.comp_percentage}%`}</Sensitive>} highlight />
                
                <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-[#F4F7FE]">
                  <span className="text-xs text-[#A3AED0]">Status</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    agentDetails.status === 'active' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-200 text-[#A3AED0]'
                  }`}>
                    {agentDetails.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Disable / Enable — visible to anyone who can see this agent's profile
                  (admin or upline). Backend re-enforces downline scoping. */}
              {agentDetails.id !== user?.id && (
                <div className="pt-3">
                  <Button
                    variant={agentDetails.status === 'active' ? 'destructive' : 'default'}
                    size="sm"
                    className="w-full"
                    data-testid={`drawer-disable-btn-${agentDetails.id}`}
                    onClick={() => {
                      setSelectedAgent(agentDetails);
                      setStatusModalOpen(true);
                    }}
                  >
                    {agentDetails.status === 'active' ? (
                      <><Ban className="h-3.5 w-3.5 mr-1.5" /> Disable Account</>
                    ) : (
                      <><CheckCircle className="h-3.5 w-3.5 mr-1.5" /> Enable Account</>
                    )}
                  </Button>
                </div>
              )}

              {/* Licensed States - Admin Only */}
              {isAdmin && agentDetails.licensed_states?.length > 0 && (
                <div className="pt-4 border-t border-[#E9EDF7]">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium text-[#2B3674]">Licensed States</span>
                    <span className="ml-auto text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                      {agentDetails.licensed_states.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {agentDetails.licensed_states.map((ls) => (
                      <div key={ls.state} className="flex items-center gap-2 p-2 rounded-lg bg-[#F4F7FE] text-sm">
                        <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                        <span className="font-medium text-[#2B3674]">{ls.state}</span>
                        {ls.license_number && <span className="text-xs text-[#A3AED0] truncate">{ls.license_number}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Appointed Carriers - Admin Only */}
              {isAdmin && agentDetails.appointed_carriers?.length > 0 && (
                <div className="pt-4 border-t border-[#E9EDF7]">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm font-medium text-[#2B3674]">Appointed Carriers</span>
                    <span className="ml-auto text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                      {agentDetails.appointed_carriers.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {agentDetails.appointed_carriers.map((carrier) => (
                      <div key={carrier.carrier_id} className="flex items-center gap-2 p-2 rounded-lg bg-[#F4F7FE]">
                        <div 
                          className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-bold"
                          style={{ backgroundColor: carrier.primary_color }}
                        >
                          {carrier.carrier_name.substring(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#2B3674] truncate">{carrier.carrier_name}</div>
                          {carrier.writing_number && <div className="text-[10px] text-[#A3AED0] font-mono">#{carrier.writing_number}</div>}
                        </div>
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Data Message - Admin Only */}
              {isAdmin && !agentDetails.licensed_states?.length && !agentDetails.appointed_carriers?.length && (
                <div className="pt-4 border-t border-[#E9EDF7]">
                  <p className="text-xs text-[#A3AED0] text-center py-4">No licenses or carrier appointments on file</p>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Admin Control Modals */}
      {isAdmin && (
        <>
          <ReassignAgentModal
            open={reassignModalOpen}
            onClose={() => setReassignModalOpen(false)}
            agent={selectedAgent}
            allUsers={allUsers}
            onConfirm={handleReassignAgent}
            loading={modalLoading}
          />
          <ChangeCommissionModal
            key={selectedAgent?.id}
            open={commissionModalOpen}
            onClose={() => setCommissionModalOpen(false)}
            agent={selectedAgent}
            onConfirm={handleChangeCommission}
            loading={modalLoading}
          />
          <ExportModal
            open={exportModalOpen}
            onClose={() => setExportModalOpen(false)}
            onConfirm={handleExportHierarchy}
            loading={modalLoading}
          />
          <DeleteUserModal
            open={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            agent={selectedAgent}
            onConfirm={handleDeleteUser}
            loading={modalLoading}
          />
          <EditAgentDetailsModal
            open={editDetailsModalOpen}
            onClose={() => setEditDetailsModalOpen(false)}
            agent={selectedAgent}
            onConfirm={handleEditAgentDetails}
            loading={modalLoading}
          />
        </>
      )}
      
      {/* Toggle Status Modal - Available for all users to manage downline */}
      <ToggleStatusModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        agent={selectedAgent}
        onConfirm={handleToggleStatus}
        loading={modalLoading}
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, mono, highlight }) {
  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${highlight ? 'bg-blue-50' : 'bg-[#F4F7FE]'}`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`h-3.5 w-3.5 ${highlight ? 'text-[#2447D6]' : 'text-[#A3AED0]'}`} />}
        <span className="text-xs text-[#A3AED0]">{label}</span>
      </div>
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'font-bold text-[#1E3FBF]' : 'text-[#2B3674]'}`}>{value}</span>
    </div>
  );
}
